"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import "./SettingsPage.css"
import API from "../services/api"
import ConfirmDialog from "../components/ConfirmDialog"
import Toast, { useToast } from "../components/Toast"

const SettingsPage = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState("username")
  const [sortAsc, setSortAsc] = useState(true)
  const [user, setUser] = useState({ username: "", email: "" })
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [newUser, setNewUser] = useState({ username: "", email: "", password: "" })
  const [currentTime, setCurrentTime] = useState(new Date())
  const [editingUser, setEditingUser] = useState(null)
  const [toasts, setToasts] = useState([])
  const { addToast, removeToast } = useToast(setToasts)
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: "", onConfirm: null })

  // Theme state with localStorage initialization
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme")
    return savedTheme ? savedTheme === "dark" : true
  })

  const fetchUsers = () => {
    API.get("/users")
      .then((res) => setUsers(res.data || []))
      .catch((err) => console.error("User fetch error", err))
  }

  useEffect(() => {
    const token = localStorage.getItem("accessToken")
    if (!token) return navigate("/")

    const userData = JSON.parse(localStorage.getItem("user") || "{}")

    const roles = Array.isArray(userData.role)
      ? userData.role
      : typeof userData.role === "string"
        ? [userData.role]
        : []

    if (!roles.includes("admin")) {
      addToast("Seul un administrateur peut accéder à cette page.", "error")
      return navigate("/dashboard")
    }

    setUser(userData)

    const loadTimer = setTimeout(() => setIsLoading(false), 800)
    const timer = setInterval(() => setCurrentTime(new Date()), 60000)

    fetchUsers()

    return () => {
      clearTimeout(loadTimer)
      clearInterval(timer)
    }
  }, [navigate, addToast])

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light")
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const handleUpdateUser = () => {
    if (!editingUser) return
    API.put(`/users/${editingUser._id}`, editingUser)
      .then(() => {
        addToast(`Utilisateur « ${editingUser.username} » mis à jour`, "success")
        setEditingUser(null)
        fetchUsers()
      })
      .catch((err) => {
        console.error("Update error", err)
        addToast("Erreur lors de la mise à jour", "error")
      })
  }

  const handleDelete = (id) => {
    const target = users.find((u) => u._id === id)
    setConfirmDialog({
      isOpen: true,
      title: "Supprimer l'utilisateur",
      message: "Cette action est irréversible. Voulez-vous vraiment supprimer :",
      itemName: target?.username,
      confirmLabel: "Supprimer",
      variant: "danger",
      onConfirm: () => {
        setConfirmDialog({ isOpen: false })
        API.delete(`/users/${id}`)
          .then(() => {
            addToast(`Utilisateur « ${target?.username} » supprimé`, "success")
            fetchUsers()
          })
          .catch((err) => {
            console.error("Delete error", err)
            addToast("Erreur lors de la suppression", "error")
          })
      },
    })
  }

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortAsc(!sortAsc)
    } else {
      setSortBy(field)
      setSortAsc(true)
    }
  }

  const handleAddUser = () => {
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      addToast("Veuillez remplir tous les champs", "error")
      return
    }
    API.post("/users", newUser)
      .then(() => {
        addToast(`Utilisateur « ${newUser.username} » créé avec succès`, "success")
        setNewUser({ username: "", email: "", password: "" })
        setShowAddUserModal(false)
        fetchUsers()
      })
      .catch((err) => {
        console.error("Add user error", err)
        addToast("Erreur lors de la création", "error")
      })
  }

  const sortedUsers = [...users]
    .filter(
      (u) =>
        u.username?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      const valA = a[sortBy] || ""
      const valB = b[sortBy] || ""
      return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA)
    })

  const formatDate = (date) => {
    const options = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    return date.toLocaleDateString("fr-FR", options)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  if (isLoading) {
    return (
      <div className={isDarkMode ? "loading-container" : "light-loading-container"}>
        <div className={isDarkMode ? "loading-logo" : "light-loading-logo"}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"} 
    alt="Guestly Logo"
    className="logo-image"
  />
</div>

        <div className={isDarkMode ? "loading-spinner" : "light-loading-spinner"}>
          <div className={isDarkMode ? "spinner-circle" : "light-spinner-circle"}></div>
          <div className={isDarkMode ? "spinner-circle-inner" : "light-spinner-circle-inner"}></div>
        </div>
        <div className={isDarkMode ? "loading-text" : "light-loading-text"}>Chargement...</div>
      </div>
    )
  }

  return (
    <div className={isDarkMode ? "dashboard" : "light-dashboard"}>
      <div className={isDarkMode ? "mobile-header" : "light-mobile-header"}>
        <button className={isDarkMode ? "menu-toggle" : "light-menu-toggle"} onClick={toggleSidebar}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className={isDarkMode ? "mobile-logo" : "light-mobile-logo"}>
          <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

        </div>
        <div className={isDarkMode ? "mobile-user" : "light-mobile-user"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
        </div>
      </div>

      <div className={`${isDarkMode ? "sidebar" : "light-sidebar"} ${isSidebarOpen ? "open" : ""}`}>
        <div className={isDarkMode ? "sidebar-header" : "light-sidebar-header"}>
          <div className={isDarkMode ? "logo" : "light-logo"}>
            <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

          </div>
          <button className={isDarkMode ? "close-sidebar" : "light-close-sidebar"} onClick={toggleSidebar}>
            ×
          </button>
        </div>

        <div className={isDarkMode ? "user-profile" : "light-user-profile"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
          <div className={isDarkMode ? "user-info" : "light-user-info"}>
            <div className={isDarkMode ? "user-name" : "light-user-name"}>{user.username}</div>
            <div className={isDarkMode ? "user-email" : "light-user-email"}>{user.email}</div>
          </div>
        </div>

        <div className={isDarkMode ? "sidebar-date" : "light-sidebar-date"}>
          <div className={isDarkMode ? "date-display" : "light-date-display"}>{formatDate(currentTime)}</div>
          <div className={isDarkMode ? "time-display" : "light-time-display"}>{formatTime(currentTime)}</div>
        </div>

        <nav className={isDarkMode ? "sidebar-nav" : "light-sidebar-nav"}>
          <ul>
            <li>
              <a href="#dashboard" onClick={() => navigate("/dashboard")}>
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🏠</span>
                <span>Tableau de bord</span>
              </a>
            </li>
            <li>
              <a href="#profile" onClick={() => navigate("/profile")}>
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>👤</span>
                <span>Mon profil</span>
              </a>
            </li>
            <li className="active">
              <a href="#settings">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>⚙️</span>
                <span>Paramètres</span>
              </a>
            </li>

          </ul>
        </nav>

        <div className={isDarkMode ? "sidebar-footer" : "light-sidebar-footer"}>
          <button
            className={isDarkMode ? "logout-button" : "light-logout-button"}
            onClick={() => {
              localStorage.removeItem("accessToken")
              localStorage.removeItem("user")
              navigate("/")
            }}
          >
            <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      <div className={isDarkMode ? "main-content" : "light-main-content"}>
        {/* ── Page header ── */}
        <div className="sp-page-header">
          <div className="sp-page-header-left">
            <div className="sp-page-icon">⚙️</div>
            <div>
              <h1 className={isDarkMode ? "sp-title-dark" : "sp-title-light"}>Paramètres</h1>
              <p className={isDarkMode ? "sp-subtitle-dark" : "sp-subtitle-light"}>
                Gérez les utilisateurs et la configuration du système
              </p>
            </div>
          </div>
          <div className="sp-header-actions">
            <button
              className={isDarkMode ? "theme-toggle-button" : "light-theme-toggle-button"}
              onClick={toggleTheme}
              title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
            >
              {isDarkMode ? "☀️" : "🌙"}
            </button>
            <div className={isDarkMode ? "user-avatar-small" : "light-user-avatar-small"}>
              {user.username.charAt(0)}
            </div>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="sp-stats-row">
          {[
            { icon: "👥", label: "Total utilisateurs", value: users.length, color: "#ec4899" },
            { icon: "🛡️", label: "Administrateurs", value: users.filter(u => (u.role||[]).includes("admin")).length, color: "#8b5cf6" },
            { icon: "📡", label: "Statut serveur", value: "En ligne", color: "#10b981" },
            { icon: "🔒", label: "Sécurité", value: "Protégée", color: "#f59e0b" },
          ].map((stat) => (
            <div key={stat.label} className={isDarkMode ? "sp-stat-card sp-stat-card-dark" : "sp-stat-card sp-stat-card-light"}>
              <div className="sp-stat-icon" style={{ background: stat.color + "22", color: stat.color }}>{stat.icon}</div>
              <div className="sp-stat-info">
                <span className={isDarkMode ? "sp-stat-value-dark" : "sp-stat-value-light"}>{stat.value}</span>
                <span className={isDarkMode ? "sp-stat-label-dark" : "sp-stat-label-light"}>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Users table card ── */}
        <div className={isDarkMode ? "sp-card sp-card-dark" : "sp-card sp-card-light"}>
          <div className="sp-card-header">
            <div className="sp-card-title-row">
              <h2 className={isDarkMode ? "sp-card-title-dark" : "sp-card-title-light"}>
                👥 Gestion des utilisateurs
              </h2>
              <span className="sp-user-count">{sortedUsers.length} utilisateur{sortedUsers.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="sp-card-toolbar">
              <div className="sp-search-wrap">
                <span className="sp-search-icon">🔍</span>
                <input
                  className={isDarkMode ? "sp-search-input sp-search-dark" : "sp-search-input sp-search-light"}
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="sp-add-btn" onClick={() => setShowAddUserModal(true)}>
                <span>+</span> Ajouter
              </button>
            </div>
          </div>

          {sortedUsers.length === 0 ? (
            <div className="sp-empty">
              <div className="sp-empty-icon">👤</div>
              <p className={isDarkMode ? "sp-empty-text-dark" : "sp-empty-text-light"}>Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th className={isDarkMode ? "sp-th-dark" : "sp-th-light"} onClick={() => handleSort("username")} style={{cursor:"pointer"}}>
                      Utilisateur {sortBy === "username" && <span>{sortAsc ? "↑" : "↓"}</span>}
                    </th>
                    <th className={isDarkMode ? "sp-th-dark" : "sp-th-light"} onClick={() => handleSort("email")} style={{cursor:"pointer"}}>
                      Email {sortBy === "email" && <span>{sortAsc ? "↑" : "↓"}</span>}
                    </th>
                    <th className={isDarkMode ? "sp-th-dark" : "sp-th-light"}>Rôles</th>
                    <th className={isDarkMode ? "sp-th-dark" : "sp-th-light"} style={{textAlign:"right"}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => (
                    <tr key={u._id} className={isDarkMode ? "sp-tr-dark" : "sp-tr-light"}>
                      <td className={isDarkMode ? "sp-td-dark" : "sp-td-light"}>
                        <div className="sp-user-cell">
                          <div className="sp-avatar">{u.username.charAt(0).toUpperCase()}</div>
                          <span className={isDarkMode ? "sp-username-dark" : "sp-username-light"}>{u.username}</span>
                        </div>
                      </td>
                      <td className={isDarkMode ? "sp-td-dark sp-email" : "sp-td-light sp-email"}>{u.email}</td>
                      <td className={isDarkMode ? "sp-td-dark" : "sp-td-light"}>
                        <div className="sp-roles-wrap">
                          {(Array.isArray(u.role) ? u.role : [u.role]).filter(Boolean).map((r) => (
                            <span key={r} className={`sp-role-badge sp-role-${r}`}>{r}</span>
                            ))}
                        </div>
                      </td>
                      <td className={isDarkMode ? "sp-td-dark" : "sp-td-light"}>
                        <div className="sp-row-actions">
                          <button className="sp-btn-edit" onClick={() => setEditingUser({ ...u })} title="Modifier">
                            ✏️
                          </button>
                          <button className="sp-btn-delete" onClick={() => handleDelete(u._id)} title="Supprimer">
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add user modal ── */}
      {showAddUserModal && (
        <div className="sp-modal-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className={isDarkMode ? "sp-modal sp-modal-dark" : "sp-modal sp-modal-light"} onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <div className="sp-modal-icon">👤</div>
              <h3 className={isDarkMode ? "sp-modal-title-dark" : "sp-modal-title-light"}>Ajouter un utilisateur</h3>
              <button className="sp-modal-close" onClick={() => setShowAddUserModal(false)}>×</button>
            </div>
            <div className="sp-modal-body">
              {[
                { label: "Nom d'utilisateur", key: "username", type: "text", placeholder: "ex: johndoe" },
                { label: "Email", key: "email", type: "email", placeholder: "ex: john@hotel.com" },
                { label: "Mot de passe", key: "password", type: "password", placeholder: "••••••••" },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key} className="sp-field">
                  <label className={isDarkMode ? "sp-label-dark" : "sp-label-light"}>{label}</label>
                  <input
                    type={type}
                    value={newUser[key]}
                    onChange={(e) => setNewUser({ ...newUser, [key]: e.target.value })}
                    placeholder={placeholder}
                    className={isDarkMode ? "sp-input sp-input-dark" : "sp-input sp-input-light"}
                  />
                </div>
              ))}
            </div>
            <div className="sp-modal-footer">
              <button className="sp-btn-cancel" onClick={() => setShowAddUserModal(false)}>Annuler</button>
              <button className="sp-btn-confirm" onClick={handleAddUser}>Créer l'utilisateur</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit user modal ── */}
      {editingUser && (
        <div className="sp-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className={isDarkMode ? "sp-modal sp-modal-dark" : "sp-modal sp-modal-light"} onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <div className="sp-modal-icon">✏️</div>
              <h3 className={isDarkMode ? "sp-modal-title-dark" : "sp-modal-title-light"}>Modifier l'utilisateur</h3>
              <button className="sp-modal-close" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <div className="sp-modal-body">
              <div className="sp-field">
                <label className={isDarkMode ? "sp-label-dark" : "sp-label-light"}>Nom d'utilisateur</label>
                <input
                  type="text"
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                  className={isDarkMode ? "sp-input sp-input-dark" : "sp-input sp-input-light"}
                />
              </div>
              <div className="sp-field">
                <label className={isDarkMode ? "sp-label-dark" : "sp-label-light"}>Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className={isDarkMode ? "sp-input sp-input-dark" : "sp-input sp-input-light"}
                />
              </div>
              <div className="sp-field">
                <label className={isDarkMode ? "sp-label-dark" : "sp-label-light"}>Rôles</label>
                <div className="sp-roles-grid">
                  {["admin", "restaurant", "seminaire", "bar", "offre"].map((roleOption) => {
                    const checked = (editingUser.role || []).includes(roleOption)
                    return (
                      <label key={roleOption} className={`sp-role-toggle ${checked ? "sp-role-toggle-on" : isDarkMode ? "sp-role-toggle-off-dark" : "sp-role-toggle-off-light"}`}>
                        <input
                          type="checkbox"
                          style={{ display: "none" }}
                          checked={checked}
                          onChange={(e) => {
                            const updatedRoles = e.target.checked
                              ? [...(editingUser.role || []), roleOption]
                              : (editingUser.role || []).filter((r) => r !== roleOption)
                            setEditingUser({ ...editingUser, role: updatedRoles })
                          }}
                        />
                        <span>{roleOption.charAt(0).toUpperCase() + roleOption.slice(1)}</span>
                        {checked && <span className="sp-role-check">✓</span>}
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className="sp-modal-footer">
              <button className="sp-btn-cancel" onClick={() => setEditingUser(null)}>Annuler</button>
              <button className="sp-btn-confirm" onClick={handleUpdateUser}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
/* ───── Settings Page custom styles ───── */

/* Page header */
.sp-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
}
.sp-page-header-left { display: flex; align-items: center; gap: 1rem; }
.sp-page-icon {
  width: 56px; height: 56px; border-radius: 16px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.6rem; box-shadow: 0 4px 14px rgba(236,72,153,0.35);
  flex-shrink: 0;
}
.sp-title-dark  { font-size: 2rem; font-weight: 800; color: #f1f5f9; margin: 0; }
.sp-title-light { font-size: 2rem; font-weight: 800; color: #1e293b; margin: 0; }
.sp-subtitle-dark  { color: #94a3b8; font-size: 0.95rem; margin: 0.2rem 0 0; }
.sp-subtitle-light { color: #64748b; font-size: 0.95rem; margin: 0.2rem 0 0; }
.sp-header-actions { display: flex; align-items: center; gap: 0.75rem; }

/* Stats row */
.sp-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;
}
.sp-stat-card {
  display: flex; align-items: center; gap: 1rem;
  padding: 1.25rem 1.5rem; border-radius: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.sp-stat-card:hover { transform: translateY(-3px); }
.sp-stat-card-dark  { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 4px 16px rgba(0,0,0,0.25); }
.sp-stat-card-light { background: #fff; border: 1px solid #e2e8f0; box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
.sp-stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
.sp-stat-info  { display: flex; flex-direction: column; }
.sp-stat-value-dark  { font-size: 1.4rem; font-weight: 700; color: #f1f5f9; line-height: 1.2; }
.sp-stat-value-light { font-size: 1.4rem; font-weight: 700; color: #1e293b; line-height: 1.2; }
.sp-stat-label-dark  { font-size: 0.8rem; color: #94a3b8; margin-top: 2px; }
.sp-stat-label-light { font-size: 0.8rem; color: #64748b; margin-top: 2px; }

/* Main card */
.sp-card { border-radius: 20px; overflow: hidden; margin-bottom: 2rem; }
.sp-card-dark  { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 8px 32px rgba(0,0,0,0.3); }
.sp-card-light { background: #fff; border: 1px solid #e2e8f0; box-shadow: 0 8px 32px rgba(0,0,0,0.06); }

.sp-card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  display: flex; flex-direction: column; gap: 1rem;
}
.sp-card-title-row { display: flex; align-items: center; gap: 0.75rem; }
.sp-card-title-dark  { font-size: 1.25rem; font-weight: 700; color: #f1f5f9; margin: 0; }
.sp-card-title-light { font-size: 1.25rem; font-weight: 700; color: #1e293b; margin: 0; }
.sp-user-count {
  padding: 0.2rem 0.7rem; border-radius: 999px;
  background: linear-gradient(45deg, rgba(236,72,153,0.15), rgba(139,92,246,0.15));
  color: #ec4899; font-size: 0.8rem; font-weight: 600;
}
.sp-card-toolbar { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.sp-search-wrap { position: relative; flex: 1; min-width: 200px; }
.sp-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 1rem; pointer-events: none; }
.sp-search-input { width: 100%; padding: 0.65rem 1rem 0.65rem 2.6rem; border-radius: 10px; font-size: 0.95rem; outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
.sp-search-dark  { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); color: #f1f5f9; }
.sp-search-dark::placeholder { color: #64748b; }
.sp-search-dark:focus  { border-color: #ec4899; box-shadow: 0 0 0 3px rgba(236,72,153,0.2); }
.sp-search-light { background: #f8fafc; border: 2px solid #e2e8f0; color: #1e293b; }
.sp-search-light:focus { border-color: #ec4899; box-shadow: 0 0 0 3px rgba(236,72,153,0.1); }
.sp-add-btn {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.65rem 1.25rem; border: none; border-radius: 10px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6); color: #fff;
  font-weight: 600; font-size: 0.95rem; cursor: pointer;
  box-shadow: 0 4px 12px rgba(236,72,153,0.3); white-space: nowrap;
  transition: all 0.2s;
}
.sp-add-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(236,72,153,0.4); }

/* Table */
.sp-table-wrap { overflow-x: auto; }
.sp-table { width: 100%; border-collapse: collapse; }
.sp-th-dark  { padding: 1rem 1.5rem; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; text-align: left; border-bottom: 1px solid rgba(255,255,255,0.06); white-space: nowrap; }
.sp-th-light { padding: 1rem 1.5rem; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b;  text-align: left; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.sp-td-dark  { padding: 1rem 1.5rem; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle; }
.sp-td-light { padding: 1rem 1.5rem; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.sp-tr-dark:hover  td { background: rgba(255,255,255,0.03); }
.sp-tr-light:hover td { background: rgba(236,72,153,0.03); }
.sp-user-cell { display: flex; align-items: center; gap: 0.75rem; }
.sp-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(236,72,153,0.3); flex-shrink: 0;
}
.sp-username-dark  { font-weight: 600; color: #f1f5f9; }
.sp-username-light { font-weight: 600; color: #1e293b; }
.sp-email { font-size: 0.9rem; }
.sp-roles-wrap { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.sp-role-badge {
  padding: 0.2rem 0.6rem; border-radius: 999px;
  font-size: 0.75rem; font-weight: 600; letter-spacing: 0.02em;
}
.sp-role-admin      { background: rgba(236,72,153,0.15); color: #ec4899; }
.sp-role-restaurant { background: rgba(245,158,11,0.15);  color: #f59e0b; }
.sp-role-seminaire  { background: rgba(59,130,246,0.15);  color: #3b82f6; }
.sp-role-bar        { background: rgba(16,185,129,0.15);  color: #10b981; }
.sp-role-offre      { background: rgba(139,92,246,0.15);  color: #8b5cf6; }
.sp-row-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.sp-btn-edit {
  padding: 0.45rem 0.7rem; border: none; border-radius: 8px; cursor: pointer;
  background: rgba(59,130,246,0.15); color: #3b82f6; font-size: 1rem;
  transition: all 0.2s;
}
.sp-btn-edit:hover { background: #3b82f6; color: #fff; transform: translateY(-1px); }
.sp-btn-delete {
  padding: 0.45rem 0.7rem; border: none; border-radius: 8px; cursor: pointer;
  background: rgba(239,68,68,0.15); color: #ef4444; font-size: 1rem;
  transition: all 0.2s;
}
.sp-btn-delete:hover { background: #ef4444; color: #fff; transform: translateY(-1px); }

/* Empty state */
.sp-empty { padding: 3rem 2rem; text-align: center; }
.sp-empty-icon { font-size: 3rem; margin-bottom: 0.75rem; opacity: 0.4; }
.sp-empty-text-dark  { color: #64748b; font-size: 1rem; }
.sp-empty-text-light { color: #94a3b8; font-size: 1rem; }

/* Modal */
.sp-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000; backdrop-filter: blur(4px); padding: 1rem;
}
.sp-modal {
  width: 100%; max-width: 480px; border-radius: 20px;
  overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,0.3);
  animation: spModalIn 0.25s ease;
}
@keyframes spModalIn {
  from { opacity: 0; transform: scale(0.95) translateY(12px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.sp-modal-dark  { background: #1e293b; border: 1px solid rgba(255,255,255,0.1); }
.sp-modal-light { background: #fff;    border: 1px solid #e2e8f0; }
.sp-modal-header {
  display: flex; align-items: center; gap: 0.75rem; padding: 1.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.07);
}
.sp-modal-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem; flex-shrink: 0;
}
.sp-modal-title-dark  { flex: 1; font-size: 1.1rem; font-weight: 700; color: #f1f5f9; margin: 0; }
.sp-modal-title-light { flex: 1; font-size: 1.1rem; font-weight: 700; color: #1e293b; margin: 0; }
.sp-modal-close {
  background: none; border: none; font-size: 1.5rem; cursor: pointer;
  color: #94a3b8; width: 32px; height: 32px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; line-height: 1;
  transition: all 0.2s;
}
.sp-modal-close:hover { background: rgba(239,68,68,0.1); color: #ef4444; }
.sp-modal-body { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.1rem; }
.sp-field { display: flex; flex-direction: column; gap: 0.4rem; }
.sp-label-dark  { font-size: 0.85rem; font-weight: 600; color: #94a3b8; }
.sp-label-light { font-size: 0.85rem; font-weight: 600; color: #64748b; }
.sp-input {
  padding: 0.7rem 1rem; border-radius: 10px; font-size: 0.95rem;
  outline: none; transition: border-color 0.2s, box-shadow 0.2s; font-family: inherit;
}
.sp-input-dark  { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); color: #f1f5f9; }
.sp-input-dark::placeholder { color: #475569; }
.sp-input-dark:focus  { border-color: #ec4899; box-shadow: 0 0 0 3px rgba(236,72,153,0.2); }
.sp-input-light { background: #f8fafc; border: 2px solid #e2e8f0; color: #1e293b; }
.sp-input-light:focus { border-color: #ec4899; box-shadow: 0 0 0 3px rgba(236,72,153,0.1); }
.sp-roles-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; }
.sp-role-toggle {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.5rem 0.75rem; border-radius: 8px; cursor: pointer;
  font-size: 0.85rem; font-weight: 500; transition: all 0.2s; user-select: none;
}
.sp-role-toggle-on { background: linear-gradient(45deg, rgba(236,72,153,0.2), rgba(139,92,246,0.2)); color: #ec4899; border: 1px solid rgba(236,72,153,0.4); }
.sp-role-toggle-off-dark  { background: rgba(255,255,255,0.05); color: #94a3b8; border: 1px solid rgba(255,255,255,0.08); }
.sp-role-toggle-off-light { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
.sp-role-check { font-size: 0.75rem; font-weight: 700; color: #ec4899; }
.sp-modal-footer {
  display: flex; justify-content: flex-end; gap: 0.75rem;
  padding: 1.25rem 1.5rem; border-top: 1px solid rgba(0,0,0,0.07);
}
.sp-btn-cancel {
  padding: 0.65rem 1.25rem; border-radius: 10px; border: 2px solid #e2e8f0;
  background: transparent; color: #64748b; font-weight: 600; cursor: pointer;
  font-size: 0.95rem; transition: all 0.2s;
}
.sp-btn-cancel:hover { border-color: #ec4899; color: #ec4899; }
.sp-btn-confirm {
  padding: 0.65rem 1.5rem; border-radius: 10px; border: none;
  background: linear-gradient(45deg, #ec4899, #8b5cf6); color: #fff;
  font-weight: 600; cursor: pointer; font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(236,72,153,0.3); transition: all 0.2s;
}
.sp-btn-confirm:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(236,72,153,0.4); }

/* Responsive */
@media (max-width: 900px) {
  .sp-stats-row { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .sp-stats-row { grid-template-columns: 1fr 1fr; }
  .sp-card-toolbar { flex-direction: column; align-items: stretch; }
  .sp-roles-grid { grid-template-columns: repeat(2, 1fr); }
  .sp-page-header { flex-direction: column; align-items: flex-start; }
}

/* Light Mode Styles for Settings Page */
.light-dashboard {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 50%, #f8fafc 100%);
  color: #1e293b;
  min-height: 100vh;
  font-family: "Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.light-loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fdf2f8 0%, #f3e8ff 50%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.modal-container,
.light-modal-container {
  background: var(--modal-bg, white);
  border-radius: 12px;
  width: 90% !important;
  max-width: 500px !important;
  max-height: 80vh !important;
  overflow: hidden !important; /* hide scroll on container itself */
  padding: 0 !important; /* remove internal padding from wrapper */
  position: relative !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  display: flex !important;
  flex-direction: column !important;
}

/* ✅ Fixed header */
.modal-header,
.light-modal-header {
  position: sticky !important;
  top: 0 !important;
  background: inherit !important;
  z-index: 10 !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #ddd !important;
}

/* ✅ Scrollable content area only */
.modal-content,
.light-modal-content {
  overflow-y: auto !important;
  flex: 1 !important; /* take remaining height */
  padding: 20px !important;
}

/* Footer area (buttons) fixed at bottom inside modal */
.modal-actions,
.light-modal-actions {
  padding: 16px 20px !important;
  border-top: 1px solid #ddd !important;
  background: inherit !important;
  position: sticky !important;
  bottom: 0 !important;
  z-index: 9 !important;
}

/* Overlay scroll safety */
.modal-overlay,
.light-modal-overlay {
  align-items: center !important;
  justify-content: center !important;
  overflow-y: auto !important;
  padding: 20px !important;
}

.light-loading-logo {
  font-size: 3rem;
  font-weight: bold;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2rem;
  animation: lightPulse 2s ease-in-out infinite;
}

.light-loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 2rem;
}

.light-spinner-circle {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid #f1f5f9;
  border-top: 4px solid #ec4899;
  border-radius: 50%;
  animation: lightSpin 1s linear infinite;
}

.light-spinner-circle-inner {
  position: absolute;
  top: 10px;
  left: 10px;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  border: 2px solid transparent;
  border-top: 2px solid #8b5cf6;
  border-radius: 50%;
  animation: lightSpin 0.8s linear infinite reverse;
}

.light-loading-text {
  font-size: 1.2rem;
  color: #64748b;
  font-weight: 500;
}

@keyframes lightPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes lightSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.light-mobile-header {
  display: none;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.light-menu-toggle {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.light-menu-toggle:hover {
  background: rgba(236, 72, 153, 0.1);
}

.light-menu-toggle span {
  width: 25px;
  height: 3px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.light-mobile-logo .light-logo-text {
  font-size: 1.5rem;
  font-weight: bold;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.light-mobile-user .light-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-sidebar {
  width: 280px;
  background: linear-gradient(180deg, #ffffff 0%, #fdf2f8 100%);
  border-right: 1px solid #e2e8f0;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.light-sidebar-header {
  padding: 2rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
}

.light-logo .light-logo-text {
  font-size: 2rem;
  font-weight: bold;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.light-close-sidebar {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.light-close-sidebar:hover {
  background: rgba(236, 72, 153, 0.1);
  color: #ec4899;
}

.light-user-profile {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(253, 242, 248, 0.8) 100%);
}

.light-user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-user-info {
  flex: 1;
}

.light-user-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.light-user-email {
  font-size: 0.875rem;
  color: #64748b;
}

.light-sidebar-date {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(253, 242, 248, 0.9) 100%);
}

.light-date-display {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.light-time-display {
  font-size: 1.5rem;
  font-weight: bold;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.light-sidebar-nav {
  flex: 1;
  padding: 1rem 0;
}

.light-sidebar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.light-sidebar-nav li {
  margin: 0.25rem 0;
}

.light-sidebar-nav a {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  color: #64748b;
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 0 25px 25px 0;
  margin-right: 1rem;
  position: relative;
}

.light-sidebar-nav a::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-radius: 0 4px 4px 0;
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.light-sidebar-nav a:hover {
  background: linear-gradient(90deg, rgba(236, 72, 153, 0.1), rgba(139, 92, 246, 0.1));
  color: #ec4899;
  transform: translateX(5px);
}

.light-sidebar-nav a:hover::before {
  transform: scaleY(1);
}

.light-sidebar-nav li.active a {
  background: linear-gradient(90deg, rgba(236, 72, 153, 0.15), rgba(139, 92, 246, 0.15));
  color: #ec4899;
  font-weight: 600;
}

.light-sidebar-nav li.active a::before {
  transform: scaleY(1);
}

.light-nav-icon {
  font-size: 1.2rem;
}

.light-sidebar-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(253, 242, 248, 0.9) 100%);
}

.light-logout-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-logout-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.4);
  background: linear-gradient(45deg, #db2777, #7c3aed);
}

.light-main-content {
  margin-left: 280px;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 30%, #f8fafc 100%);
}

.light-welcome-section {
  margin-bottom: 2rem;
}

.light-welcome-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.light-welcome-header h1 {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1e293b;
  margin: 0;
}

.light-wave-emoji {
  animation: lightWave 2s ease-in-out infinite;
}

@keyframes lightWave {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-10deg); }
}

.light-welcome-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
}

.light-action-button {
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.light-action-button::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 2px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-radius: 50%;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.light-action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.2);
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
}

.light-action-icon {
  font-size: 1.2rem;
  z-index: 1;
}

.light-theme-toggle-button {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-size: 1.2rem;
  position: relative;
}

.light-theme-toggle-button::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 2px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-radius: 50%;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.light-theme-toggle-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.2);
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
}

.theme-toggle-button {
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-size: 1.2rem;
  position: relative;
  color: #e2e8f0;
}

.theme-toggle-button::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 2px;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-radius: 50%;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.theme-toggle-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.2);
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
}

.light-user-avatar-small {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-welcome-section p {
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
}

/* Dashboard Grid */
.light-dashboard-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

/* Services Section */
.light-services-section {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Activity Section */
.light-activity-section {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.light-activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.light-activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(253, 242, 248, 0.8) 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.light-activity-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.1);
}

/* Search and Filter Components */
.light-search-filter-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(253, 242, 248, 0.8) 100%);
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.light-search-container {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.light-search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  color: #1e293b;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.light-search-input:focus {
  outline: none;
  border-color: #ec4899;
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
}

/* Section Headers */
.light-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.light-section-header h2 {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1e293b;
  margin: 0;
}

.light-section-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-section-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.4);
  background: linear-gradient(45deg, #db2777, #7c3aed);
}

/* Users Table */
.light-users-table-container {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(253, 242, 248, 0.9) 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.light-users-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.light-users-table th {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  color: #1e293b;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.light-users-table th:hover {
  background: linear-gradient(45deg, rgba(236, 72, 153, 0.1), rgba(139, 92, 246, 0.1));
  color: #ec4899;
}

.light-users-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  color: #64748b;
}

.light-users-table tbody tr {
  transition: all 0.3s ease;
}

.light-users-table tbody tr:hover {
  background: linear-gradient(90deg, rgba(236, 72, 153, 0.05), rgba(139, 92, 246, 0.05));
}

.light-user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.light-user-avatar-mini {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(236, 72, 153, 0.3);
}

/* Table Action Buttons - Works for both themes */
.table-action {
  padding: 0.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 0.25rem;
  position: relative;
  z-index: 1;
}

.table-action.edit {
  background: linear-gradient(45deg, #3b82f6, #1d4ed8);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.table-action.edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.table-action.delete {
  background: linear-gradient(45deg, #ef4444, #dc2626);
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.table-action.delete:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.light-no-results {
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  padding: 2rem;
}

/* Modal Styles */
.light-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.light-modal-container {
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.light-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
}

.light-modal-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
}

.light-close-modal {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.light-close-modal:hover {
  background: rgba(236, 72, 153, 0.1);
  color: #ec4899;
}

.light-modal-content {
  padding: 1.5rem;
}

.light-form-group {
  margin-bottom: 1.5rem;
}

.light-form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #1e293b;
  font-weight: 500;
}

.light-form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
  color: #1e293b;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: inherit;
}

.light-form-input:focus {
  outline: none;
  border-color: #ec4899;
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
}

.light-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.light-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1e293b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.light-checkbox-label:hover {
  background: rgba(236, 72, 153, 0.05);
}

.light-checkbox-label input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  accent-color: #ec4899;
}

.light-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(253, 242, 248, 0.9) 100%);
}

.light-cancel-button {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.light-cancel-button:hover {
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
  border-color: transparent;
}

.light-download-button {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
}

.light-download-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(236, 72, 153, 0.4);
  background: linear-gradient(45deg, #db2777, #7c3aed);
}

@media (max-width: 768px) {
  .light-mobile-header {
    display: flex;
  }

  .light-sidebar {
    transform: translateX(-100%);
  }

  .light-sidebar.open {
    transform: translateX(0);
  }

  .light-close-sidebar {
    display: block;
  }

  .light-main-content {
    margin-left: 0;
    padding: 1rem;
  }

  .light-welcome-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .light-welcome-header h1 {
    font-size: 2rem;
  }

  .light-dashboard-grid {
    grid-template-columns: 1fr;
  }

  .light-search-filter-container {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 480px) {
  .light-welcome-header h1 {
    font-size: 1.5rem;
  }

  .light-users-table {
    font-size: 0.875rem;
  }

  .light-users-table th,
  .light-users-table td {
    padding: 0.75rem 0.5rem;
  }
}

/* Enhanced focus states for accessibility */
.light-action-button:focus,
.light-theme-toggle-button:focus,
.light-section-action:focus,
.table-action:focus {
  outline: 2px solid #ec4899;
  outline-offset: 2px;
}

/* Smooth transitions for theme switching */
* {
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}
`}</style>

      <ConfirmDialog
        isOpen={!!confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        itemName={confirmDialog.itemName}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        isDarkMode={isDarkMode}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false })}
      />
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  )
}

export default SettingsPage
